import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

interface ToastAction {
  label: string;
  onClick?: () => void;
  variant?: 'primary' | 'default';
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
  actions?: ToastAction[];
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Toast Provider
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration || 5000
    };

    setToasts(prev => [...prev, newToast]);

    // Auto remove toast after duration
    if (!newToast.persistent && newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  }, [removeToast]);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearAllToasts }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Hook do używania toastów
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Komponenty pomocnicze dla różnych typów toastów
export const useToastHelpers = () => {
  const { addToast } = useToast();

  const success = useCallback((title: string, message?: string) => {
    addToast({ type: 'success', title, message });
  }, [addToast]);

  const error = useCallback((title: string, message?: string) => {
    addToast({ type: 'error', title, message });
  }, [addToast]);

  const warning = useCallback((title: string, message?: string) => {
    addToast({ type: 'warning', title, message });
  }, [addToast]);

  const info = useCallback((title: string, message?: string) => {
    addToast({ type: 'info', title, message });
  }, [addToast]);

  return useMemo(() => ({
    success,
    error,
    warning,
    info
  }), [success, error, warning, info]);
};

// Komponent pojedynczego toast
const ToastItem: React.FC<{ toast: Toast; onRemove: () => void }> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animacja wejścia
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleRemove = () => {
    setIsLeaving(true);
    setTimeout(onRemove, 300); // Czas na animację wyjścia
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-var(--color-success)" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-var(--color-danger)" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-var(--color-warning)" />;
      case 'info':
        return <Info className="w-5 h-5 text-var(--color-primary)" />;
    }
  };

  const getBackgroundColor = () => {
    switch (toast.type) {
      case 'success':
        return 'border-l-green-600 bg-green-50';
      case 'error':
        return 'border-l-red-600 bg-red-50';
      case 'warning':
        return 'border-l-amber-600 bg-amber-50';
      case 'info':
        return 'border-l-gray-600 bg-gray-50';
    }
  };

  return (
    <div
      className={`
        w-full max-w-sm card glass border-l-4 ${getBackgroundColor()}
        transition-all duration-300 ease-out transform
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${isLeaving ? 'scale-95' : 'scale-100'}
      `}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-var(--text-primary)">
              {toast.title}
            </p>
            {toast.message && (
              <p className="text-sm text-var(--text-secondary) mt-1">
                {toast.message}
              </p>
            )}
          </div>
          <button
            onClick={handleRemove}
            className="flex-shrink-0 btn btn-ghost w-6 h-6 p-0 text-var(--text-tertiary) hover:text-var(--text-primary)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {toast.actions && toast.actions.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            {toast.actions.map((a, idx) => (
              <button
                key={idx}
                onClick={() => { try { a.onClick && a.onClick(); } finally { handleRemove(); } }}
                className={`px-3 py-1.5 rounded text-xs ${a.variant === 'primary' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Kontener toastów
const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  if (typeof window === 'undefined') return null;

  return createPortal(
    <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem
            toast={toast}
            onRemove={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>,
    document.body
  );
};

// Progress Toast dla długotrwałych operacji
export const ProgressToast: React.FC<{
  title: string;
  progress: number;
  onCancel?: () => void;
}> = ({ title, progress, onCancel }) => {
  return (
    <div className="w-full max-w-sm card glass">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-var(--text-primary)">
            {title}
          </span>
          {onCancel && (
            <button
              onClick={onCancel}
              className="btn btn-ghost w-6 h-6 p-0 text-var(--text-tertiary)"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          ></div>
        </div>
        <div className="text-xs text-var(--text-secondary) mt-1">
          {Math.round(progress)}% ukończone
        </div>
      </div>
    </div>
  );
};

export default ToastProvider;
